import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getCategories, getMachines } from "../httpService";
import { buildMachineProperties } from "../machineUtils";
import { categoryUrlSlug } from "../slug.js";
import { getSnapshotValue, hasSnapshotValue } from "../snapshot.js";
import { UserContext } from "./UserContext";

const MachineContext = React.createContext();

const bySortOrderThenName = (a, b) => {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  return a.name.localeCompare(b.name);
};

const sortCategories = (categories) => categories.sort(bySortOrderThenName);

/**
 * Turn a raw machine list into the derived category/manufacturer/build-year state.
 * Shared by the fetch and by the server snapshot so seeded pages get identical filters.
 */
const deriveMachineState = (machinesData, publicCategories) => {
  const { foundCategories, foundManufacturers, foundBuildYearInterval } =
    buildMachineProperties(machinesData);

  const categoryList = sortCategories(
    Object.keys(foundCategories).map((key) => {
      const slug = (foundCategories[key].slug || "").toLowerCase();
      const cat = publicCategories.find((c) => (c.slug || "").toLowerCase() === slug);
      return {
        name: key,
        slug: foundCategories[key].slug,
        // The landing page URL. From /categories where we have the row, transliterated from the
        // primary key where we don't, so a link is never built out of undefined.
        urlSlug: categoryUrlSlug(cat ?? { slug: foundCategories[key].slug }),
        amountOfRelatedMachines: foundCategories[key].count,
        sortOrder: cat?.sortOrder ?? 0,
        imageUrl: cat?.imageUrl ?? "",
      };
    })
  );

  // The full list from /categories, including the ones no machine currently uses: a category
  // landing page has to be able to render its name and description with an empty grid rather
  // than 404 the moment the last machine in it is sold. Only category/manufacturer pages read
  // this, and they always carry the real machine list (server/page.js), so counting from it
  // here — unlike categoryList's counterpart below for the home page — is always accurate.
  const counts = new Map(
    Object.values(foundCategories).map((entry) => [(entry.slug || "").toLowerCase(), entry.count])
  );
  const allCategories = sortCategories(
    publicCategories.map((category) => ({
      name: category.name || category.slug,
      slug: category.slug,
      urlSlug: categoryUrlSlug(category),
      description: category.description || "",
      imageUrl: category.imageUrl || "",
      sortOrder: category.sortOrder ?? 0,
      amountOfRelatedMachines: counts.get((category.slug || "").toLowerCase()) ?? 0,
    }))
  );

  return {
    categoryList,
    allCategories,
    manufacturerList: Object.keys(foundManufacturers).map((key) => ({
      name: key,
      amountOfRelatedMachines: foundManufacturers[key],
    })),
    buildYearInterval: foundBuildYearInterval,
  };
};

/**
 * The same category list, built from the categories alone.
 *
 * Pages that show category cards or the header dropdown but no machines (the home page) inline
 * `categories` with a `machineCount` instead of the whole machine list — see
 * server/page.js. Until the client's own fetch lands, that is what the filters are built from.
 */
const deriveCategoriesFromCounts = (publicCategories) =>
  sortCategories(
    publicCategories.map((category) => ({
      name: category.name || category.slug,
      slug: category.slug,
      urlSlug: categoryUrlSlug(category),
      amountOfRelatedMachines: category.machineCount ?? 0,
      sortOrder: category.sortOrder ?? 0,
      imageUrl: category.imageUrl ?? "",
    }))
  );

/** Initial derived state: from the seeded machines when there are any, from the counts if not. */
const seedDerivedState = () => {
  const categories = getSnapshotValue("categories", []);

  if (hasSnapshotValue("machines")) {
    return deriveMachineState(getSnapshotValue("machines", []), categories);
  }

  return {
    ...deriveMachineState([], categories),
    categoryList: deriveCategoriesFromCounts(categories),
  };
};

const MachineContextProvider = (props) => {
  const [machines, setMachines] = useState(() => getSnapshotValue("machines", []));
  // Whether the list has been looked up at all, which an empty `machines` cannot tell you on its
  // own: a page rendered from a snapshot that inlined no machine — /sv/machines/<dead id> — has
  // looked and found nothing, while a page that has not fetched yet simply does not know. Only
  // the first is a 404, and the server render depends on telling them apart.
  const [machinesLoaded, setMachinesLoaded] = useState(() => hasSnapshotValue("machines"));
  // Derived filter state is kept in one object so the server snapshot can seed all of it
  // from a single derivation.
  const [derived, setDerived] = useState(seedDerivedState);
  const { categoryList, allCategories, manufacturerList, buildYearInterval } = derived;

  const { hasRole, initialized, locale } = useContext(UserContext);
  const adminData = hasRole("admin");

  const initializeData = useCallback(async () => {
    try {
      const dataLocale = adminData ? "sv" : locale;
      const [machinesData, publicCategories] = await Promise.all([
        getMachines(dataLocale),
        getCategories(dataLocale),
      ]);

      setDerived(deriveMachineState(machinesData, publicCategories));
      setMachines(machinesData);
      setMachinesLoaded(true);
    } catch {
      // Fetch failed silently — page still renders with defaults
    }
  }, [adminData, locale]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adminData intentionally triggers re-fetch for admin data
  useEffect(() => {
    if (initialized) {
      initializeData();
    }
  }, [initialized, adminData, initializeData]);

  const value = useMemo(
    () => ({
      machines,
      machinesLoaded,
      machineCategories: categoryList,
      categories: allCategories,
      machineManufacturers: manufacturerList,
      buildYearInterval,
      reinitialize: initializeData,
    }),
    [
      machines,
      machinesLoaded,
      categoryList,
      allCategories,
      manufacturerList,
      buildYearInterval,
      initializeData,
    ]
  );

  return <MachineContext.Provider value={value} {...props} />;
};

export { MachineContextProvider, MachineContext };
